import type {FancyPageSidebarItem} from '@wandb/weave/components/FancyPage';
import {IconNames} from '@wandb/weave/components/Icon';
import _ from 'lodash';
import {useMemo} from 'react';
import {useLocation} from 'react-router-dom';

export const useProjectSidebar = (
  isLoading: boolean,
  viewingRestricted: boolean,
  hasModelsData: boolean,
  hasWeaveData: boolean,
  hasTraceBackend: boolean = true,
  hasModelsAccess: boolean = true,
  isLaunchActive: boolean = false,
  isWandbAdmin: boolean = false
): FancyPageSidebarItem[] => {
  // must preserve named workspace url query string
  // when navigating between workspace & runs pages
  let nwSearchStr: string | undefined;
  const location = useLocation();
  const allSearchParams = new URLSearchParams(location.search);
  const nwId = allSearchParams.get('nw');
  if (nwId) {
    const nwSearchParams = new URLSearchParams();
    nwSearchParams.append('nw', nwId);
    nwSearchStr = nwSearchParams.toString();
  }

  // Should show models sidebar items if we have models data or if we don't have a trace backend
  let showModelsSidebarItems = hasModelsData || !hasTraceBackend;
  // Should show weave sidebar items if we have weave data and we have a trace backend
  let showWeaveSidebarItems = hasWeaveData && hasTraceBackend;

  let isModelsOnly = showModelsSidebarItems && !showWeaveSidebarItems;
  let isWeaveOnly = !showModelsSidebarItems && showWeaveSidebarItems;

  if (!hasModelsAccess) {
    showModelsSidebarItems = false;
    isModelsOnly = false;

    showWeaveSidebarItems = true;
    isWeaveOnly = true;
  }

  const isNoSidebarItems = !showModelsSidebarItems && !showWeaveSidebarItems;
  const isBothSidebarItems = showModelsSidebarItems && showWeaveSidebarItems;
  const isShowAll = isNoSidebarItems || isBothSidebarItems;

  // If Weave + Models: Put Leaders, Threads, Mods in a More button
  // If Only Weave, Put nothing in more button
  return useMemo(() => {
    const weaveObjectMenu = [
      {
        type: 'button' as const,
        name: 'All Assets',
        slug: 'weave/assets',
        additionalSlugs: ['weave/objects', 'weave/object-versions'],
      },
      {
        type: 'button' as const,
        name: 'Models',
        slug: 'weave/models',
        iconName: IconNames.Model,
      },
      {
        type: 'button' as const,
        name: 'Datasets',
        slug: 'weave/datasets',
        iconName: IconNames.Table,
      },
      {
        type: 'button' as const,
        name: 'Prompts',
        slug: 'weave/prompts',
        iconName: IconNames.ForumChatBubble,
      },
      {
        type: 'button' as const,
        name: 'Scorers',
        slug: 'weave/scorers',
        iconName: IconNames.TypeNumberAlt,
      },
      {
        type: 'button' as const,
        name: 'Evaluations',
        slug: 'weave/evaluation-definitions',
        iconName: IconNames.BaselineAlt,
      },
      {
        type: 'button' as const,
        name: 'Ops',
        slug: 'weave/operations',
        additionalSlugs: ['weave/op-versions'],
        iconName: IconNames.JobProgramCode,
      },
      {
        type: 'button' as const,
        name: 'Other',
        slug: 'weave/custom-objects',
        iconName: IconNames.CategoryMultimodal,
      },
    ];
    const allItems = isLoading
      ? []
      : [
          {
            type: 'button' as const,
            name: 'Project',
            slug: 'overview',
            isShown: true,
            iconName: IconNames.Info,
          },
          {
            type: 'divider' as const,
            key: 'dividerOverview',
            isShown: isShowAll,
          },
          {
            type: 'label' as const,
            label: 'Models',
            isShown: isShowAll,
          },
          {
            type: 'button' as const,
            name: 'Workspace',
            slug: 'workspace',
            search: nwSearchStr,
            isShown: !isWeaveOnly,
            isDisabled: viewingRestricted,
            iconName: IconNames.DashboardBlackboard,
          },
          {
            type: 'button' as const,
            name: 'Runs',
            slug: 'table',
            search: nwSearchStr,
            isShown: !isWeaveOnly,
            isDisabled: viewingRestricted,
            iconName: IconNames.Table,
          },
          {
            type: 'button' as const,
            name: 'Jobs',
            slug: 'jobs',
            isShown: isModelsOnly && isLaunchActive,
            isDisabled: viewingRestricted,
            iconName: IconNames.FlashBolt,
          },
          {
            type: 'button' as const,
            name: 'Automat.', // Truncation per design
            nameTooltip: 'Automations',
            slug: 'automations',
            isShown: isModelsOnly,
            isDisabled: viewingRestricted,
            iconName: IconNames.JobAutomation,
          },
          {
            type: 'button' as const,
            name: 'Sweeps',
            slug: 'sweeps',
            isShown: isModelsOnly,
            isDisabled: viewingRestricted,
            iconName: IconNames.SweepsBroom,
          },
          {
            type: 'button' as const,
            name: 'Reports',
            slug: 'reportlist',
            isShown: isModelsOnly,
            isDisabled: viewingRestricted,
            iconName: IconNames.Report,
          },
          {
            type: 'button' as const,
            name: 'Artifacts',
            slug: 'artifacts',
            isShown: isModelsOnly,
            iconName: IconNames.VersionsLayers,
            isDisabled: viewingRestricted,
          },
          {
            type: 'menuPlaceholder' as const,
            isShown: isShowAll,
            key: 'moreModels',
            menu: ['jobs', 'automations', 'sweeps', 'reportlist', 'artifacts'],
          },
          {
            type: 'divider' as const,
            key: 'dividerModelsWeave',
            isShown: isShowAll,
          },
          {
            type: 'label' as const,
            label: 'Weave',
            isShown: isShowAll,
          },
          {
            type: 'button' as const,
            name: 'Traces',
            slug: 'weave/traces',
            isShown: showWeaveSidebarItems || isShowAll,
            iconName: IconNames.LayoutTabs,
          },
          {
            type: 'button' as const,
            name: 'Evals',
            slug: 'weave/evaluations',
            isShown: showWeaveSidebarItems || isShowAll,
            iconName: IconNames.BaselineAlt,
          },
          {
            type: 'button' as const,
            name: 'Playground',
            slug: 'weave/playground',
            isShown: showWeaveSidebarItems || isShowAll,
            iconName: IconNames.VideoPlay,
          },
          {
            type: 'button' as const,
            name: 'Monitors',
            slug: 'weave/monitors',
            isShown: showWeaveSidebarItems || isShowAll,
            iconName: IconNames.JobAutomation,
          },
          {
            type: 'button' as const,
            name: 'Leaders',
            slug: 'weave/leaderboards',
            isShown: isWeaveOnly, // Goes in more menu when both
            iconName: IconNames.BenchmarkSquare,
          },
          {
            type: 'button' as const,
            name: 'Threads',
            slug: 'weave/threads',
            isShown: isWeaveOnly, // Goes in more menu when both
            iconName: IconNames.Thread,
          },
          {
            type: 'sidebarMenu' as const,
            name: 'Assets',
            slug: 'weave/assets',
            additionalSlugs: ['weave/objects', 'weave/object-versions'],
            menu: weaveObjectMenu,
            isShown: showWeaveSidebarItems || isShowAll, // Always show Objects button
            iconName: IconNames.FolderAlt,
          },
          {
            type: 'button' as const,
            name: 'Mods',
            slug: 'weave/mods',
            // isDisabled: !isWandbAdmin,
            isShown: isWeaveOnly && isWandbAdmin, // Goes in more menu when both
            iconName: IconNames.LayoutGrid,
          },
          // Ask scott about this, make sure it only shows when it used to
          {
            type: 'menuPlaceholder' as const,
            isShown: isShowAll,
            key: 'moreWeave',
            menu: ['weave/threads', 'weave/leaderboards', 'weave/mods'],
          },
        ];

    const indexedItems = _.keyBy(allItems, 'slug');

    // Expand menu items
    const expandedItems = allItems.map(item => {
      if (item.type !== 'menuPlaceholder') {
        return item;
      }
      // TODO: Probably need to filter this by shown too
      return {
        ...item,
        type: 'menu',
        menu: item.menu.map(menuItemId => indexedItems[menuItemId]),
      };
    });

    // Filter out items that are not shown.
    const onlyShownItems = expandedItems
      .filter(({isShown}) => isShown)
      .map(({isShown, ...item}) => item) as FancyPageSidebarItem[];

    return onlyShownItems;
  }, [
    isLaunchActive,
    isLoading,
    isModelsOnly,
    isShowAll,
    isWandbAdmin,
    isWeaveOnly,
    nwSearchStr,
    showWeaveSidebarItems,
    viewingRestricted,
  ]);
};
